import { Utils } from '../utils/Utils.js';

export class SignalingClient extends EventTarget {
    constructor() {
        super();
        this.ws = null;
        this.isConnected = false;
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.reconnectDelay = 1000;
        this.currentRoomId = null;
        this.timeouts = new Set(); // Track all timeouts for cleanup

        // For development, we'll use a local WebSocket signaling server
        // In production, this would connect to a Cloudflare Worker
        this.isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        this.signalingUrl = this.isDevelopment ?
            'ws://localhost:8080' :
            'wss://p2p-video-chat-signaling.ragiop07.workers.dev';
        this.useWebSocket = true; // Try WebSocket first, fallback to mock if needed
    }

    async connect() {
        return new Promise((resolve, reject) => {
            try {
                // For development, try WebSocket first, fallback to mock
                if (this.isDevelopment && !this.useWebSocket) {
                    this.setupMockSignaling();
                    resolve();
                    return;
                }

                // Set a timeout for WebSocket connection
                const connectionTimeout = this.createTimeout(() => {
                    Utils.warn('WebSocket connection timeout, falling back to mock signaling');
                    if (this.ws) {
                        this.ws.close();
                        this.ws = null;
                    }
                    this.setupMockSignaling();
                    resolve();
                }, 5000); // 5 second timeout

                this.ws = new WebSocket(this.signalingUrl);

                this.ws.onopen = () => {
                    this.clearTimeout(connectionTimeout);
                    Utils.info('Signaling server connected');
                    this.isConnected = true;
                    this.reconnectAttempts = 0;
                    resolve();
                };

                this.ws.onmessage = (event) => {
                    try {
                        const message = JSON.parse(event.data);
                        this.handleMessage(message);
                    } catch (error) {
                        Utils.error('Failed to parse signaling message:', error);
                    }
                };

                this.ws.onclose = () => {
                    Utils.warn('Signaling server disconnected');
                    this.isConnected = false;
                    this.attemptReconnect();
                };

                this.ws.onerror = (error) => {
                    this.clearTimeout(connectionTimeout);
                    Utils.error('Signaling server error:', error);
                    Utils.error('WebSocket URL:', this.signalingUrl);
                    Utils.info('Falling back to mock signaling...');
                    this.setupMockSignaling();
                    resolve();
                };

            } catch (error) {
                Utils.error('Failed to create WebSocket connection:', error);
                Utils.info('Falling back to mock signaling...');
                this.setupMockSignaling();
                resolve();
            }
        });
    }

    setupMockSignaling() {
        // For development, we'll simulate signaling using localStorage and events
        Utils.info('Using mock signaling for development');
        this.isConnected = true;

        // Listen for messages from other tabs/windows
        window.addEventListener('storage', (e) => {
            if (e.key && e.key.startsWith('signaling_')) {
                const roomId = e.key.replace('signaling_', '');
                if (roomId === this.currentRoomId && e.newValue) {
                    try {
                        const message = JSON.parse(e.newValue);
                        // Don't process our own messages
                        if (message.sender !== this.getClientId()) {
                            this.createTimeout(() => this.handleMessage(message), 100);
                        }
                    } catch (error) {
                        Utils.error('Failed to parse mock signaling message:', error);
                    }
                }
            }
        });

        // Simulate connection success
        setTimeout(() => {
            this.dispatchEvent(new CustomEvent('connected'));
        }, 100);
    }

    getClientId() {
        if (!this.clientId) {
            // Generate secure client ID
            const randomValues = new Uint8Array(6);
            crypto.getRandomValues(randomValues);
            this.clientId = Array.from(randomValues)
                .map(b => b.toString(36).padStart(2, '0'))
                .join('')
                .substring(0, 9);
        }
        return this.clientId;
    }

    /**
     * Create a tracked timeout that can be cleaned up
     * @param {Function} callback
     * @param {number} delay
     * @returns {number} timeout ID
     */
    createTimeout(callback, delay) {
        const timeoutId = setTimeout(() => {
            this.timeouts.delete(timeoutId);
            callback();
        }, delay);
        this.timeouts.add(timeoutId);
        return timeoutId;
    }

    /**
     * Clear a specific timeout
     * @param {number} timeoutId
     */
    clearTimeout(timeoutId) {
        clearTimeout(timeoutId);
        this.timeouts.delete(timeoutId);
    }

    /**
     * Clear all tracked timeouts
     */
    clearAllTimeouts() {
        this.timeouts.forEach(timeoutId => clearTimeout(timeoutId));
        this.timeouts.clear();
    }

    sendMessage(message) {
        message.sender = this.getClientId();
        message.timestamp = Date.now();

        if (this.isDevelopment || !this.isConnected) {
            // For mock signaling, don't echo messages back to the same user
            // This prevents the feedback loop that was causing WebRTC errors
            Utils.debug('Mock signaling: message sent but not echoed back to prevent loop');
        } else if (this.ws && this.isConnected) {
            this.ws.send(JSON.stringify(message));
        } else {
            Utils.error('Signaling server not connected');
        }
    }

    handleMessage(message) {
        Utils.debug('Received signaling message:', message.type);

        switch (message.type) {
        case 'room-created':
            this.dispatchEvent(new CustomEvent('roomCreated', { detail: message.roomId }));
            break;

        case 'room-joined':
            this.dispatchEvent(new CustomEvent('roomJoined', { detail: message.roomId }));
            break;

        case 'peer-joined':
            this.dispatchEvent(new CustomEvent('peerJoined', { detail: message.peerId }));
            break;

        case 'peer-left':
            this.dispatchEvent(new CustomEvent('peerLeft', { detail: message.peerId }));
            break;

        case 'offer':
            this.dispatchEvent(new CustomEvent('offer', { detail: message.offer }));
            break;

        case 'answer':
            this.dispatchEvent(new CustomEvent('answer', { detail: message.answer }));
            break;

        case 'ice-candidate':
            this.dispatchEvent(new CustomEvent('iceCandidate', { detail: message.candidate }));
            break;

        case 'error':
            this.dispatchEvent(new CustomEvent('error', { detail: message.error }));
            break;

        default:
            Utils.warn('Unknown signaling message type:', message.type);
        }
    }

    async createRoom(roomId, roomName = null) {
        this.currentRoomId = roomId;
        this.currentRoomName = roomName;

        this.sendMessage({
            type: 'create-room',
            roomId: roomId,
            roomName: roomName
        });

        // For mock signaling, simulate room creation (works in both dev and production when WebSocket fails)
        if (this.isDevelopment || !this.isConnected) {
            this.createTimeout(() => {
                this.dispatchEvent(new CustomEvent('roomCreated', { detail: { roomId, roomName } }));
                // Don't automatically trigger peer connection - let real users connect
            }, 100);
        }
    }

    async joinRoom(roomInput) {
        this.currentRoomId = roomInput; // This will be updated once we know the actual room ID
        this.sendMessage({
            type: 'join-room',
            roomInput: roomInput
        });

        // For mock signaling, simulate joining (works in both dev and production when WebSocket fails)
        if (this.isDevelopment || !this.isConnected) {
            this.createTimeout(() => {
                // Extract room name from input (could be URL, room name, or room ID)
                let roomName = roomInput;
                let roomId = roomInput;

                // Handle URL format
                if (roomInput.includes('room=')) {
                    try {
                        const url = new URL(roomInput.includes('://') ? roomInput : `https://${roomInput}`);
                        roomName = url.searchParams.get('room') || roomInput;
                        roomId = roomName;
                    } catch (error) {
                        Utils.error('Failed to parse room URL:', error);
                    }
                }

                this.dispatchEvent(new CustomEvent('roomJoined', { detail: { roomId, roomName } }));
                // Don't automatically trigger peer connection - let real users connect
            }, 100);
        }
    }

    sendOffer(offer) {
        this.sendMessage({
            type: 'offer',
            offer: offer
        });
    }

    sendAnswer(answer) {
        this.sendMessage({
            type: 'answer',
            answer: answer
        });
    }

    sendIceCandidate(candidate) {
        this.sendMessage({
            type: 'ice-candidate',
            candidate: candidate
        });
    }

    leaveRoom() {
        if (this.currentRoomId) {
            this.sendMessage({
                type: 'leave-room',
                roomId: this.currentRoomId
            });
            this.currentRoomId = null;
        }
    }

    disconnect() {
        if (this.currentRoomId) {
            this.leaveRoom();
        }

        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }

        this.isConnected = false;
    }

    attemptReconnect() {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            Utils.error('Max reconnection attempts reached');
            this.dispatchEvent(new CustomEvent('error', {
                detail: new Error('Failed to reconnect to signaling server')
            }));
            return;
        }

        this.reconnectAttempts++;
        const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

        Utils.info(`Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`);

        this.createTimeout(() => {
            this.connect().catch(error => Utils.error('Reconnection failed:', error));
        }, delay);
    }

    // Mock signaling helper method - simplified for testing
    simulateMockPeerResponse(message) {
        if ((!this.isDevelopment && this.isConnected) || !this.currentRoomId) return;

        // For testing, we'll simulate a successful connection after a short delay
        // This bypasses the complex WebRTC handshake simulation
        this.createTimeout(() => {
            // Only simulate peer joining if this is a room creation, not room joining
            if (message.type === 'create-room') {
                this.dispatchEvent(new CustomEvent('peerJoined', { detail: 'mock-peer' }));

                // After a bit more delay, simulate connection established
                this.createTimeout(() => {
                    // Dispatch a custom event that the WebRTC manager can listen for
                    this.dispatchEvent(new CustomEvent('mockConnectionEstablished'));
                }, 500);
            }
        }, 300);
    }

    /**
     * Clean up resources and close connections
     */
    cleanup() {
        // Clear all timeouts
        this.clearAllTimeouts();

        // Close WebSocket connection
        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }

        // Reset state
        this.isConnected = false;
        this.currentRoomId = null;
        this.currentRoomName = null;
        this.reconnectAttempts = 0;
    }

    // Event helper methods - removed to ensure consistent event handling
    // Use addEventListener directly instead
}
